import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  audioPlaybackReducer,
  createInitialAudioPlaybackState,
} from "../utils/audio-playback-reducer";
import { getAudioDurationSeconds } from "../utils/get-audio-duration-seconds";
import { getClampedSeekTimeSeconds } from "../utils/get-clamped-seek-time-seconds";
import { getClampedSkipTimeSeconds } from "../utils/get-clamped-skip-time-seconds";
import { getClampedVolume } from "../utils/get-clamped-volume";
import { isAbortError } from "../utils/is-abort-error";
import { useAudioElementEvents } from "./use-audio-element-events";

type UseAudioPlaybackEventHandlers = {
  onEnded?: (audio: HTMLAudioElement) => void;
  onError?: (audio: HTMLAudioElement) => void;
  onLoadedMetadata?: (audio: HTMLAudioElement) => void;
  onPause?: (audio: HTMLAudioElement) => void;
  onPlay?: (audio: HTMLAudioElement) => void;
  onWaiting?: (audio: HTMLAudioElement) => void;
};

type UseAudioPlaybackOptions = {
  audioSource: string;
  eventHandlers?: UseAudioPlaybackEventHandlers;
  initialDurationSeconds?: number;
};

function useAudioPlayback({
  audioSource,
  eventHandlers,
  initialDurationSeconds = 0,
}: UseAudioPlaybackOptions) {
  const audioElementReference = useRef<HTMLAudioElement | null>(null);
  const durationSecondsReference = useRef(initialDurationSeconds);
  const eventHandlersReference = useRef(eventHandlers);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null,
  );
  const [state, dispatch] = useReducer(
    audioPlaybackReducer,
    initialDurationSeconds,
    createInitialAudioPlaybackState,
  );

  useEffect(() => {
    eventHandlersReference.current = eventHandlers;
  }, [eventHandlers]);

  useEffect(() => {
    durationSecondsReference.current = state.durationSeconds;
  }, [state.durationSeconds]);

  const resolveDuration = useCallback(
    (audio: HTMLAudioElement): number | undefined => {
      return (
        getAudioDurationSeconds(audio) ??
        (durationSecondsReference.current > 0
          ? durationSecondsReference.current
          : undefined)
      );
    },
    [],
  );

  const audioRef = useCallback((element: HTMLAudioElement | null) => {
    audioElementReference.current = element;
    setAudioElement(element);
  }, []);

  useEffect(() => {
    const element = audioElementReference.current;

    if (!element || !audioSource) {
      return;
    }

    if (element.getAttribute("src") !== audioSource) {
      element.src = audioSource;
    }

    dispatch({
      initialDurationSeconds,
      type: "reset",
    });
    durationSecondsReference.current = initialDurationSeconds;
  }, [audioElement, audioSource, initialDurationSeconds]);

  const seekToSeconds = useCallback(
    (nextTimeSeconds: number): number | undefined => {
      const audio = audioElementReference.current;
      const duration = audio ? resolveDuration(audio) : undefined;

      if (!audio || duration === undefined) {
        return undefined;
      }

      const safeTimeSeconds = getClampedSeekTimeSeconds(
        nextTimeSeconds,
        duration,
      );

      if (safeTimeSeconds === undefined) {
        return undefined;
      }

      audio.currentTime = safeTimeSeconds;
      dispatch({
        currentTimeSeconds: safeTimeSeconds,
        type: "seeked",
      });
      return safeTimeSeconds;
    },
    [resolveDuration],
  );

  const skipBySeconds = useCallback(
    (offsetSeconds: number): number | undefined => {
      const audio = audioElementReference.current;

      if (!audio) {
        return undefined;
      }

      const safeTimeSeconds = getClampedSkipTimeSeconds(
        audio.currentTime,
        offsetSeconds,
        resolveDuration(audio),
      );

      if (safeTimeSeconds === undefined) {
        return undefined;
      }

      audio.currentTime = safeTimeSeconds;
      dispatch({
        currentTimeSeconds: safeTimeSeconds,
        type: "seeked",
      });
      return safeTimeSeconds;
    },
    [resolveDuration],
  );

  const togglePlayPause = useCallback(() => {
    const audio = audioElementReference.current;

    if (!audio || state.hasError) {
      return;
    }

    if (!audio.paused) {
      audio.pause();
      return;
    }

    void audio.play().catch((error: unknown) => {
      if (isAbortError(error)) {
        return;
      }

      dispatch({ type: "play-failed" });
      eventHandlersReference.current?.onError?.(audio);
    });
  }, [state.hasError]);

  const setPlaybackVolume = useCallback((nextVolume: number) => {
    const audio = audioElementReference.current;
    const safeVolume = getClampedVolume(nextVolume);

    if (!audio || safeVolume === undefined) {
      return;
    }

    audio.volume = safeVolume;
    dispatch({
      type: "volume-changed",
      volume: safeVolume,
    });
  }, []);

  useAudioElementEvents({
    audioElement,
    audioSource,
    dispatch,
    eventHandlersReference,
  });

  return {
    audioElement,
    audioRef,
    currentTimeSeconds: state.currentTimeSeconds,
    durationSeconds: state.durationSeconds,
    hasError: state.hasError,
    isPlaying: state.isPlaying,
    isReady: state.isReady,
    seekToSeconds,
    setPlaybackVolume,
    skipBySeconds,
    togglePlayPause,
    volume: state.volume,
  };
}

export { useAudioPlayback };
